/**
 * Responsibility: decide which slots hold an airfield, which tile builds each one, and what space it reserves.
 *
 * The arrangement of a field — runways, tower, aircraft, and whether the ground will carry them — belongs to
 * `airfieldLayout.js`, and the lines it is drawn with to `airfieldGeometry.js`.
 *
 * ## An installation, by the same test the telescope arrays passed
 *
 * It needs *level* ground rather than a new kind of ground, so it is something placed on a biome rather than a biome of its
 * own. Baseline hosts it alone: it is the flattest ground in the world, it has no structures of its own so nothing
 * competes for the space, and open country is where airstrips are. One host only, because two biomes placing sites on
 * independent grids could put two airfields on top of each other across a transition.
 *
 * ## The runway is never graded into the ground
 *
 * See `airfieldGeometry.js` for the reasoning, which is the load-bearing decision in the whole feature: the surface sits at
 * one constant height and an embankment is drawn down to the real ground. What that leaves for placement is a single
 * flatness budget rather than a terrain capability.
 *
 * ## One authoritative site rule, consulted two ways, split by what it needs
 *
 * `siteAt(slot)` decides whether a slot holds an airfield and where its centre is, **from the rolls alone**. The layout then
 * works out the rest and needs the terrain. Placement calls both; the reservation calls only `siteAt`, which is all a
 * circle around the centre requires — and a circle is rotation-invariant, which is why orientation is allowed to be
 * surveyed from the ground while position is not.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createLocalRng, deriveSeed } from './rng.js';
import { buildAirfield } from './airfieldGeometry.js';
import { fitToGround, layoutFor, runwayCountFor, SITE_REACH } from './airfieldLayout.js';
import { AIRFIELD } from '../config.js';

/** Linear interpolation, for turning a 0..1 roll into a range. */
function lerp(from, to, t) {
  return from + (to - from) * t;
}

/** Slot index a coordinate falls in. */
function slotAt(coordinate, spacing) {
  return Math.round(coordinate / spacing - 0.5);
}

/**
 * How far a site may be jittered from its slot centre.
 *
 * Clamped against the grid regardless of the configured value, so raising `SITE_JITTER` past what the spacing allows
 * cannot let two airfields overlap. That guarantee is arithmetic rather than a collision check, as it is for the city's
 * streets and the telescope arrays' sites.
 */
const SITE_JITTER = Math.max(
  0,
  Math.min(AIRFIELD.SITE_JITTER, (AIRFIELD.SLOT_SPACING - 2 * (SITE_REACH + AIRFIELD.CLEARANCE)) / 2),
);

/** Furthest any part of a site can be from its slot centre. Sets how wide the reservation has to look. */
const SLOT_INFLUENCE = SITE_JITTER + SITE_REACH + AIRFIELD.CLEARANCE;

/**
 * Build the shared site rule.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId - Derives an independent stream from everything else in the world.
 * @returns {(slotX: number, slotZ: number, sampleWeight: Function) => object|null}
 */
function createSites(seed, biomeId) {
  const siteSeed = deriveSeed(seed, `airfields:${biomeId}`);

  return function siteAt(slotX, slotZ, sampleWeight) {
    const centerX = (slotX + 0.5) * AIRFIELD.SLOT_SPACING;
    const centerZ = (slotZ + 0.5) * AIRFIELD.SLOT_SPACING;

    const weight = sampleWeight(centerX, centerZ);
    if (weight <= 0) return null;

    /*
      One stream per slot, keyed by slot index rather than by tile, so an airfield does not depend on which tile is being
      built or the order tiles are visited in.

      Roll order is part of the world's identity. Reordering these, or inserting one before an existing one, moves and
      re-aims every airfield at every seed. Append only.
    */
    const random = createLocalRng(siteSeed, slotX, slotZ);

    const presenceRoll = random();
    const jitterRollX = random();
    const jitterRollZ = random();
    const countRoll = random();
    const lengthRoll = random();

    /*
      A fixed number of slack rolls whatever the runway count, so the rolls that come after do not shift when the count
      changes. Drawing a variable number would make the aircraft depend on the number of runways, which is a surprising
      coupling to leave lying around in a deterministic world.
    */
    const slackRolls = [];
    for (let i = 0; i < AIRFIELD.MAX_RUNWAYS; i += 1) slackRolls.push(random());

    const aircraftRoll = random();
    const aircraftStationRoll = random();
    const aircraftFlipRoll = random();
    const towerSideRoll = random();

    /*
      Density scales with biome weight, so a field is less likely near a region's edge than in the middle of it. Unlike a
      scatter this does not thin an airfield out — a site either happens or it does not.
    */
    if (presenceRoll >= AIRFIELD.DENSITY * weight) return null;

    return {
      x: centerX + (jitterRollX * 2 - 1) * SITE_JITTER,
      z: centerZ + (jitterRollZ * 2 - 1) * SITE_JITTER,
      reach: SITE_REACH,
      weight,

      /*
        Everything the layout needs that does not come from the terrain. Kept as rolls rather than as finished geometry so
        that this function stays callable without a terrain sampler — which is the whole reason the reservation can answer
        at any coordinate for the price of one biome-field sample.
      */
      count: runwayCountFor(countRoll),
      primaryLength: lerp(AIRFIELD.MIN_RUNWAY, AIRFIELD.MAX_RUNWAY, lengthRoll),
      slackRolls,
      aircraftRoll,
      aircraftStationRoll,
      aircraftFlipRoll,
      towerSideRoll,
    };
  };
}

/** Range of slots whose sites could reach into a region. A site is far wider than a tile gap, so this is not the "centre falls inside" rule. */
function slotsNear(region) {
  const spacing = AIRFIELD.SLOT_SPACING;

  return {
    firstX: slotAt(region.originX - SLOT_INFLUENCE, spacing),
    lastX: slotAt(region.originX + region.size + SLOT_INFLUENCE, spacing),
    firstZ: slotAt(region.originZ - SLOT_INFLUENCE, spacing),
    lastZ: slotAt(region.originZ + region.size + SLOT_INFLUENCE, spacing),
  };
}

/**
 * The largest field the ground at a site will carry, trying the rolled runway count and then fewer.
 *
 * **Building what fits beats rejecting the site**, for three reasons. It is what a real siting survey does — you lay the
 * strips the land allows. It stops the terrain filter quietly overriding the runway-count weights: a crosswind runway needs
 * its *own* flat footprint, so measured, only one field in 35 came out with three runways when a failed layout meant no
 * airfield at all, against a configured one in five. And it costs nothing, because a shorter layout is strictly easier
 * ground than the one just rejected.
 *
 * Deterministic despite being a search: the order is fixed and no extra rolls are drawn.
 *
 * @returns {{layout: object, ground: object}|null}
 */
function largestThatFits(site, sampleGroundDetail) {
  for (let count = site.count; count >= 1; count -= 1) {
    const layout = layoutFor(site, count, sampleGroundDetail);
    const ground = fitToGround(layout, sampleGroundDetail);
    if (ground) return { layout, ground };
  }

  return null;
}

/**
 * Build the airfield placement function for a biome.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId
 * @returns {(region: object, context: object) => Array<object>}
 */
export function createAirfields(seed, biomeId) {
  const siteAt = createSites(seed, biomeId);

  /**
   * @param {{originX: number, originZ: number, size: number}} region - Tile bounds.
   * @param {object} context
   * @param {(x: number, z: number) => number} context.sampleWeight - This biome's weight.
   * @param {(x: number, z: number) => {elevation: number, slope: number}} context.sampleGroundDetail
   * @param {(x: number, z: number, radius: number) => boolean} [context.isReserved]
   * @returns {Array<object>} One structure per airfield, holding every segment it is made of.
   */
  return function collectStructures(region, { sampleWeight, sampleGroundDetail, isReserved }) {
    const slots = slotsNear(region);
    const structures = [];

    for (let slotX = slots.firstX; slotX <= slots.lastX; slotX += 1) {
      for (let slotZ = slots.firstZ; slotZ <= slots.lastZ; slotZ += 1) {
        const site = siteAt(slotX, slotZ, sampleWeight);
        if (!site) continue;

        /*
          **Ownership first, before any terrain sampling.** One tile builds a whole airfield — the field is wider than a
          tile, so splitting it per part the way the telescope arrays split their dishes would mean re-deriving the shared
          surface height in every tile it touches. Checking this first also means the neighbouring tiles that can see this
          site pay one biome-field sample for it rather than forty terrain samples.
        */
        if (site.x < region.originX || site.x >= region.originX + region.size) continue;
        if (site.z < region.originZ || site.z >= region.originZ + region.size) continue;

        /*
          Cheapest terrain test next: one sample rejects a site on an obvious hillside before the forty a footprint costs.
          It is only a pre-filter — `fitToGround` is what actually decides whether the ground will do.
        */
        if (sampleGroundDetail(site.x, site.z).slope > AIRFIELD.MAX_GROUND_SLOPE) continue;

        const fitted = largestThatFits(site, sampleGroundDetail);
        if (!fitted) continue;

        const { layout, ground } = fitted;

        // Keep the whole installation clear of another biome's structures, tested at the centre and at every runway end.
        const probes = [[site.x, site.z]];
        for (const runway of layout.runways) {
          const alongX = Math.cos(runway.heading);
          const alongZ = Math.sin(runway.heading);
          const half = runway.length / 2;
          probes.push([runway.x + alongX * half, runway.z + alongZ * half]);
          probes.push([runway.x - alongX * half, runway.z - alongZ * half]);
        }

        if (probes.some(([px, pz]) => isReserved?.(px, pz, AIRFIELD.RUNWAY_WIDTH))) continue;

        const sampleGround = (px, pz) => sampleGroundDetail(px, pz).elevation;

        const airfield = {
          x: site.x,
          z: site.z,
          weight: site.weight,
          runways: layout.runways,
          aircraft: layout.aircraft,
          deckY: ground.deckY,
          tower: { ...layout.tower, groundY: sampleGround(layout.tower.x, layout.tower.z) },
        };

        structures.push({
          kind: 'airfield',
          ...airfield,

          /** Relief across the field, so the tallest bank drawn is this plus `SURFACE_RISE`. Carried for tests and measurement. */
          drop: ground.drop,

          /*
            Built here rather than in `structureKinds.js` because the embankments need the ground, and the terrain sampler
            only exists at collect time. Water does the same thing for the same reason.
          */
          lines: buildAirfield(airfield, sampleGround),
        });
      }
    }

    return structures;
  };
}

/**
 * Build the test that says whether a point is inside an airfield.
 *
 * Published as a biome reservation so trees, towers, rock formations and farm structures all keep off the runways. Like the
 * telescope clearance it ignores the ground checks, so it reserves space around a handful of sites that were never built —
 * which costs a tree here and there and buys a test that needs no terrain sampling and answers at any coordinate.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId
 * @returns {(x: number, z: number, radius: number, context: object) => boolean}
 */
export function createAirfieldClearance(seed, biomeId) {
  const siteAt = createSites(seed, biomeId);

  return function isInsideAnAirfield(x, z, radius, { sampleWeight, cache }) {
    const spacing = AIRFIELD.SLOT_SPACING;

    /*
      Only the slots whose site could actually reach this point.

      **This is the difference between the feature being free and costing milliseconds a tile.** Every tree, rock column
      and farm structure in the world asks this question once, so the arithmetic here runs hundreds of times per tile. The
      telescope arrays learned that at five milliseconds a forest tile.
    */
    const margin = SLOT_INFLUENCE + radius;
    const firstX = slotAt(x - margin, spacing);
    const lastX = slotAt(x + margin, spacing);
    const firstZ = slotAt(z - margin, spacing);
    const lastZ = slotAt(z + margin, spacing);

    for (let slotX = firstX; slotX <= lastX; slotX += 1) {
      for (let slotZ = firstZ; slotZ <= lastZ; slotZ += 1) {
        const key = `airfield:${slotX},${slotZ}`;

        // Memoised per tile, and **not optional** — see the note above.
        let site = cache?.get(key);

        if (site === undefined) {
          site = siteAt(slotX, slotZ, sampleWeight);
          cache?.set(key, site);
        }

        if (!site) continue;

        /*
          A circle over the whole field rather than a strip per runway. Generous, and deliberately so: open ground around
          an airfield is what a real one has, and the alternative is three strip tests per query on the hottest path in the
          project.
        */
        if (Math.hypot(x - site.x, z - site.z) < site.reach + AIRFIELD.CLEARANCE + radius) return true;
      }
    }

    return false;
  };
}

/**
 * Re-exported so callers and tests have one import for the feature.
 *
 * `SITE_REACH` is derived from the layout's own dimensions and lives with them; `headingSeparation` is the authority on what
 * "the same direction" means, and the rule and its tests must not be able to disagree about that.
 */
export { headingSeparation, SITE_REACH } from './airfieldLayout.js';
